/**
 * Rock-paper-scissors — the first entry in the mini-game registry.
 *
 * Best of one, deliberately. A duel freezes both players in the middle of a 20 Hz action game,
 * so the whole exchange has to fit inside a couple of seconds; a best-of-three would triple the
 * freeze and turn a moment of tension into a menu. A draw is a draw, and the pair simply bounces
 * apart with the mutual immunity window every duel ends on.
 */

import type { MiniGame, MiniGameChoiceArt, MiniGameVerdict } from './minigame.js';

export const RPS_ROCK = 'rock';
export const RPS_PAPER = 'paper';
export const RPS_SCISSORS = 'scissors';

/** What each choice beats. One direction only — the reverse is read off it by `compare`. */
const BEATS: Record<string, string> = {
  [RPS_ROCK]: RPS_SCISSORS,
  [RPS_PAPER]: RPS_ROCK,
  [RPS_SCISSORS]: RPS_PAPER,
};

/**
 * The three drawings, in one 32-unit box so a renderer needs no per-choice size table.
 *
 * They are deliberately *objects* — a boulder, a sheet, a pair of scissors — rather than the
 * hands the emoji glyphs use: a hand at 32 px is a blob, and a blob is the one thing this game
 * already has plenty of.
 */
const ROCK_ART: MiniGameChoiceArt = {
  viewBox: '0 0 32 32',
  strokeWidth: 2,
  paths: [
    // A lopsided seven-sided boulder, then one crack across it. A symmetrical outline with
    // facets meeting in the middle reads as a cut gem rather than as a rock.
    'M5 22 L9 13 L16 8.5 L24 11 L27 19 L22 26 L11 26.5 Z',
    'M9 13 L17 16 L22 26',
  ],
};

const PAPER_ART: MiniGameChoiceArt = {
  viewBox: '0 0 32 32',
  strokeWidth: 2,
  paths: [
    // A sheet with the corner turned down, which is what makes it a page and not a rectangle.
    'M8 4 H19 L25 10 V28 H8 Z',
    'M19 4 V10 H25',
    'M12 16 H21',
    'M12 21 H21',
  ],
};

const SCISSORS_ART: MiniGameChoiceArt = {
  viewBox: '0 0 32 32',
  strokeWidth: 2,
  paths: [
    // Two blades crossing above two ring handles. The rings are arcs rather than a solid dot,
    // because the hole is what tells a pair of scissors from a pair of lollipops.
    'M9 5 L22 22.2',
    'M23 5 L10 22.2',
    'M20.3 24.5 a3.2 3.2 0 1 0 6.4 0 a3.2 3.2 0 1 0 -6.4 0',
    'M5.3 24.5 a3.2 3.2 0 1 0 6.4 0 a3.2 3.2 0 1 0 -6.4 0',
  ],
};

export const rockPaperScissors: MiniGame = {
  id: 'rps',
  name: 'Rock Paper Scissors',
  /**
   * Both answers stay off the wire until the duel reveals, and for this game that is a
   * correctness requirement rather than a flourish: a client that could read its opponent's pick
   * off its own socket would win every duel it entered.
   *
   * Declared rather than left to the default, even though the default is exactly this. It is the
   * whole of what this game gained when secrecy stopped being a global rule in `toDuelSnapshot`
   * and became a property of the game — nothing below changed by a byte — and a rule this load
   * bearing should be readable in the file it applies to.
   */
  secretUntilLocked: true,
  /**
   * What the instructions page says about this game, in this game's own file.
   *
   * Words and no markup — see `MiniGameHowToPlay`. The *pictures* on that page are the three
   * `MiniGameChoiceArt` drawings below, mounted through the same renderer the duel overlay uses,
   * so the prose never has to describe an icon that has since been redrawn.
   */
  howToPlay: {
    summary:
      'Best of one, thrown at the same moment. Rock blunts scissors, scissors cut paper, paper covers rock.',
    rules: [
      'You are both frozen for ten seconds and nobody else can touch either of you while it runs.',
      'Neither pick goes on the wire until you have both locked in, so nobody can read yours and answer it.',
      'Say nothing and the room picks for you when the clock runs out. Pick something.',
      'The winner takes a quarter of the loser. A draw costs neither of you anything.',
      'You both walk out with a few seconds of the same shield a new player gets — long enough to get clear.',
    ],
  },
  choices: [
    { id: RPS_ROCK, label: 'Rock', glyph: '✊', art: ROCK_ART },
    { id: RPS_PAPER, label: 'Paper', glyph: '✋', art: PAPER_ART },
    { id: RPS_SCISSORS, label: 'Scissors', glyph: '✌️', art: SCISSORS_ART },
  ],
  compare(a, b): MiniGameVerdict {
    if (a === b) return 0;
    if (BEATS[a] === b) return 1;
    if (BEATS[b] === a) return -1;
    // Unreachable through the simulation, which validates both choices before they are stored.
    // Answered as a draw rather than thrown, because a duel that cannot be scored must still
    // end — a throw here would take the room's tick down with it.
    return 0;
  },
  autoChoice(roll) {
    const index = Math.floor(roll * 3);
    return this.choices[index < 0 ? 0 : index > 2 ? 2 : index].id;
  },
};
